import React, { memo, useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import Button from "@material-ui/core/Button"
import { Dialog, DialogActions, DialogContent, Slide, TextField } from "@material-ui/core"
import ReactQuagga, { useQuagga } from "../ReactQuagga"
import { isHex } from "../../utils/validation"
import ReactQrReader from "../ReactQrReader/ReactQrReader"
import api, { discardStatuses, HttpMethod } from "../../constants/api"
import offlineStorage from "../../offlineStorage"
import NodeLocationNavigator from "../LayoutMap/LayoutOverlay/NodeLocationNavigator"
import SelectComponent from "./SelectComponent"
import useStyles from "./styles"
import RememberSelectionBtn from "./RememberSelectionBtn"
import ScannerHeaderComponent from "./ScannerHeaderComponent"
import scannerBtnOptions from "./ScannerBtnOptions"

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />
})

const DeviceIdInput = memo(
	({
		handleClose,
		handleAccept,
		handleComponentSelection,
		commissioningComponentSelection,
		rememberComponentSelection,
		handleRememberComponentSelection,
		locIds,
		scannerSupported,
		deviceIdEntryMethod,
		setDeviceIdEntryMethod,
		isDeviceIdAssignment,
		nodeDetails,
		masterDetails,
		handleNodeComponentScanned,
		handleMasterComponentScanned,
		nodeComponentScanned,
		masterComponentScanned,
		nodeLocIdFromUrl,
		layoutId,
		graphQlResponse,
	}) => {
		const styles = useStyles()
		const locIdsSplit = useMemo(() => (locIds.length ? locIds.split(":") : []), [locIds])
		// Info: nodeLocId follows this format - 3 characters with the index e.g. 010.
		const [_, masterUuid, nodeLocId] = locIdsSplit
		const isMaster = useMemo(() => locIdsSplit.length === 2, [locIdsSplit])
		const label = useMemo(() => (isMaster ? "Master" : "Node"), [isMaster])
		const [scanning, setScanning] = useState(false)
		const [scannedData, setScannedData] = useState()
		const [nodeIdReplacement, setNodeIdReplacement] = useState(false)
		const [newDeviceId, setNewDeviceId] = useState("")
		const masterOrNode = isMaster ? "Master" : "Node"
		const [userMessage, setUserMessage] = useState({})

		// Info: Displays the Master name or Node ID selected for scanning
		const nodeControllerUuidToDisplay = useMemo(() => {
			if (!nodeDetails || !nodeDetails[masterUuid] || !nodeDetails[masterUuid][nodeLocIdFromUrl]) return "N/A"
			return nodeDetails[masterUuid][nodeLocIdFromUrl]["nId"] ? nodeDetails[masterUuid][nodeLocIdFromUrl]["nId"] : "N/A"
		}, [masterUuid, nodeDetails, nodeLocIdFromUrl])

		const setFeedbackHandler = useCallback((response) => {
			setUserMessage(response)
		}, [])

		// Info: Handles the selection of a component
		const setComponentSelection = useCallback(
			(component, status) => {
				const scannedComponent = { ...component }
				scannedComponent.timestamp = new Date().toLocaleTimeString()
				scannedComponent.status = status
				scannedComponent.name = commissioningComponentSelection.name

				if (isMaster) {
					scannedComponent.master = masterUuid
					handleMasterComponentScanned({
						componentsScanned: [scannedComponent],
						componentType: commissioningComponentSelection.name,
					})
				}
				if (!isMaster && nodeDetails[masterUuid][nodeLocIdFromUrl]) {
					scannedComponent.node = nodeDetails[masterUuid][nodeLocIdFromUrl].uuid
					handleNodeComponentScanned({
						componentsScanned: [scannedComponent],
						componentType: commissioningComponentSelection.name,
					})
				}
			},
			[
				commissioningComponentSelection,
				isMaster,
				nodeDetails,
				masterUuid,
				nodeLocIdFromUrl,
				handleMasterComponentScanned,
				handleNodeComponentScanned,
			],
		)

		// Info: Handles the selection of a component from component list. Passed to child components as a prop
		const componentListSelectionHandler = useCallback(
			(option) => {
				setScanning(true)
				handleComponentSelection(option)
				setDeviceIdEntryMethod("qrCode")
				setNodeIdReplacement(false)
			},
			[handleComponentSelection, setDeviceIdEntryMethod],
		)

		// Info: Handles the selection between Barcode and QR code scanning. Passed to child components as a prop
		const scannerBtnOptionSelectionHandler = useCallback(
			(type, option) => {
				setScanning(true)
				setDeviceIdEntryMethod(type)
				setNodeIdReplacement(true)
				handleComponentSelection(option)
			},
			[handleComponentSelection, setDeviceIdEntryMethod],
		)

		// Info: handles the text input of the Node ID field. Used in endAdornment useMemo
		const handleSetDeviceId = (deviceId) => {
			setNewDeviceId(deviceId ?? "")
		}

		// Info: must be named 'endAdornment' for the QR code & Barcode icons to appear in the Node Id text input
		const endAdornment = useMemo(() => {
			return scannerBtnOptions(newDeviceId, handleSetDeviceId, scannerSupported, scannerBtnOptionSelectionHandler)
		}, [newDeviceId, scannerBtnOptionSelectionHandler, scannerSupported])

		// Info: Handles the change of the Node ID field
		const handleIdChange = useCallback((e) => {
			let newValue = e.target.value
			if (newValue) {
				// Remove all non-hex characters
				newValue = newValue.replace("0x", "").toUpperCase()
				newValue = newValue.replace(/[^A-F0-9]/g, "")
			}
			setNewDeviceId(newValue)
		}, [])

		useEffect(() => {
			// Info: clears the device ID entry and scanned data when switching between controllers.
			setNewDeviceId("")
			setScannedData(null)
		}, [nodeLocId])

		const inputValidationErrorText = useMemo(() => {
			if (!newDeviceId) return
			const isValid = isHex(newDeviceId)

			if (!isValid) {
				return "Not a valid hex ID."
			}

			if (!isMaster && newDeviceId.length > 16) {
				return "Max 16 characters."
			}

			if (isMaster && newDeviceId.length < 16) {
				return "Min length of 16 characters."
			}

			if (isMaster && newDeviceId.length > 18) {
				return "Max length of 18 characters."
			}
		}, [newDeviceId, isMaster])

		const inputFormattingError = useMemo(() => {
			if (inputValidationErrorText || !newDeviceId) return true
			const formatter = (val) => val.toUpperCase().padStart(8, "0")

			if (newDeviceId.length > 8) {
				const firstHalf = newDeviceId.slice(0, 8).padStart(8, "0")
				const secondHalf = newDeviceId.slice(8, 16).padStart(8, "0")
				if (formatter(firstHalf) !== firstHalf || formatter(secondHalf) !== secondHalf) {
					return true
				}
			} else {
				if (formatter(newDeviceId) !== newDeviceId.padStart(8, "0")) return true
			}
		}, [inputValidationErrorText, newDeviceId])

		const inputValidationHelpText = useMemo(() => {
			if (inputValidationErrorText) return inputValidationErrorText

			if (newDeviceId === null || inputFormattingError) {
				return `${label} ID - no leading '0x'`
			}
		}, [inputValidationErrorText, newDeviceId, inputFormattingError, label])

		const nodeIdDuplicateWarning = useMemo(() => {
			if (!newDeviceId || isMaster) return

			const slicedDeviceId = newDeviceId?.slice(-4)
			if (slicedDeviceId && slicedDeviceId !== "0000") {
				const thisMasterNodeDetails = nodeDetails?.[masterUuid] || {}

				const takenNodeIds = Object.values(thisMasterNodeDetails).map((nodeInfo) =>
					(nodeInfo["nId"] || "0000").slice(-4),
				)

				if (takenNodeIds.includes(slicedDeviceId)) {
					const matchingNode = Object.entries(thisMasterNodeDetails).find(([_nodeIndex, nodeInfo]) => {
						return (nodeInfo["nId"] || "0000").slice(-4) === slicedDeviceId && _nodeIndex !== nodeLocIdFromUrl
					})?.[0]

					if (matchingNode === undefined || matchingNode === nodeLocIdFromUrl) {
						// Info: The matching node is *this* node.
						return
					}

					setFeedbackHandler({
						message:
							"This Node ID is already taken in this Master by another Node. You cannot create duplicate IDs within a Master as it can lead to unexpected behaviors.",
						type: "error",
					})
					return true
				} else {
					setFeedbackHandler({})
				}
			}
		}, [newDeviceId, isMaster, nodeDetails, masterUuid, nodeLocIdFromUrl, setFeedbackHandler])

		const masterIdDuplicateWarning = useMemo(() => {
			if (!isMaster) return

			const mIds = Object.values(masterDetails)
				.filter((detail) => detail.mId !== masterDetails?.[masterUuid]?.mId)
				.map((detail) => detail.mId)

			if (!mIds.includes(newDeviceId)) return

			setFeedbackHandler({
				message: "This Master ID is already taken by another Master.",
				type: "error",
			})
			return true
			// We want to exclude Master details and Master UUID from the deps because as they update they will trigger
			// this memo to check the new ID.
			// eslint-disable-next-line
		}, [isMaster, newDeviceId, setFeedbackHandler])

		const handleScanError = useCallback((err) => {
			if (err.message.includes("denied")) {
				alert("Camera permission denied")
			}
			setScanning(false)
		}, [])

		const handleScannerReOpen = useCallback(() => {
			setTimeout(() => {
				setScanning(true)
			}, 1000)
		}, [])

		const handleScannerClose = useCallback(() => {
			setScanning(false)
		}, [])

		const handleScannerOpen = useCallback((status) => {
			setScanning(status)
		}, [])

		// Info: handle re-opening of scanner when another node is selected
		useEffect(() => {
			if (!isDeviceIdAssignment) return
			if (rememberComponentSelection && commissioningComponentSelection.isMaster === isMaster) {
				setScanning(true)
			}
		}, [commissioningComponentSelection, isDeviceIdAssignment, isMaster, rememberComponentSelection])

		const handleCodeDetection = useCallback(
			(data) => {
				setUserMessage({})
				if (deviceIdEntryMethod === "barCode") {
					// Create a synthetic event object, so we can reuse the logic in handleChange for scans
					const e = {
						target: { value: data.codeResult.code },
					}
					handleIdChange(e)
				} else {
					const e = {
						target: { value: data["radioid"] },
					}
					// Old functionality for updating the Radio ID in the old system with DynamoDB
					if (nodeIdReplacement) {
						handleIdChange(e)
					}
					setScannedData(data)
					// Info: sets scanning to false after a scan attempt. if unsuccessful scanner should be re-opened.
					setScanning(false)
				}
			},
			[deviceIdEntryMethod, handleIdChange, nodeIdReplacement],
		)

		// Info: trigger the REST API Master/Node importer after assigning an ID
		const triggerImporter = useCallback(async () => {
			await api[isMaster ? "triggerMasterImporterForSpecificSite" : "triggerNodeImporterForSpecificMaster"](
				isMaster ? layoutId : masterUuid,
			).catch((ex) => console.error(`Error triggering ${isMaster ? "Master" : "Node"} importer.`, ex))
		}, [isMaster, layoutId, masterUuid])

		useEffect(() => {
			return () => {
				// Info: when this component is closed we want to trigger the importer
				void triggerImporter()
			}
		}, [triggerImporter])

		const controllerUuid = useMemo(() => {
			return isMaster ? masterUuid : nodeDetails[masterUuid][nodeLocIdFromUrl].uuid
		}, [isMaster, masterUuid, nodeDetails, nodeLocIdFromUrl])

		// Info: when the "device id assignment" overlay is selected and the REST API UUID for a Node selected to scan
		// hasn't been returned from the API Nodes endpoint then scanning and saving to PostgreSQL won't be allowed.
		useEffect(() => {
			if (!controllerUuid) {
				setFeedbackHandler({
					message: "The UUID for this Node has not been loaded and as a result cannot be scanned.",
					type: "error",
				})
			} else {
				setFeedbackHandler({})
			}
		}, [controllerUuid, setFeedbackHandler])

		// Info: this function handles the GraphQL API call.
		const handleAcceptHandler = useCallback(() => {
			if (!newDeviceId) return

			if (isMaster) {
				handleAccept(newDeviceId)
			} else {
				handleAccept(newDeviceId, nodeLocIdFromUrl)
			}
			setFeedbackHandler({
				message: graphQlResponse.message,
				type: graphQlResponse.status,
			})
		}, [graphQlResponse, handleAccept, isMaster, newDeviceId, nodeLocIdFromUrl, setFeedbackHandler])

		// Info: This useEffect is triggered when a scan is made. It then sends a request to the API. Within the logic
		// there is a call to the GraphQL handler handleAcceptHandler if the component selected to scan is a Node board.
		// PostgreSQL - Saves component information
		useEffect(() => {
			if (!commissioningComponentSelection || !scannedData || !controllerUuid || nodeIdDuplicateWarning) {
				return
			}

			// Info: selection of the Node Enclosure (QR code btn within the text input box) will also trigger the
			// submission of the radio ID to GraphQL which is the legacy functionality
			if (commissioningComponentSelection.method === "setNodeEnclosure") {
				// Triggers GraphQL Node ID update. The updated ID in DynamoDB is later imported into PostgreSQL.
				handleAcceptHandler()
			}

			// Scanned data validation
			let missingFields = []
			for (const field of commissioningComponentSelection?.validation) {
				if (!scannedData[field]) {
					missingFields.push(field)
				}
			}
			if (missingFields.length) {
				setComponentSelection(commissioningComponentSelection, "failed")
				const missingFieldsString = missingFields.join(", ")
				const snackbarMessage = `${commissioningComponentSelection?.name} is missing fields: ${missingFieldsString}`
				setFeedbackHandler({
					message: snackbarMessage,
					type: "error",
				})
				handleScannerReOpen()
			}

			const controllerType = isMaster ? "master" : "node"
			scannedData[controllerType] = controllerUuid
			// part_number is always supposed to be _part_number
			// part_number is the FK in the db to an actual type, which this isn't always guaranteed to match
			scannedData["_part_number"] = scannedData["part_number"]
			delete scannedData["part_number"]
			if (scannedData["radioid"]) {
				scannedData["original_radio_id"] = scannedData["radioid"].replace("0x", "").toUpperCase()
				delete scannedData["radioid"]
			}

			if (!missingFields.length) {
				api[commissioningComponentSelection.method](controllerUuid, scannedData)
					.then((response) => {
						if (response?.status === 201) {
							setComponentSelection(commissioningComponentSelection, "success")
							setFeedbackHandler({
								message: `Successfully scanned ${commissioningComponentSelection.name} ${
									scannedData["serial_number"] ?? ""
								}`,
								type: "success",
							})
						} else {
							setFeedbackHandler({
								message: `Failed to save ${commissioningComponentSelection.name} information.`,
								type: "error",
							})
						}
					})
					.catch(async (error) => {
						const dontReopenCondition =
							!commissioningComponentSelection.isMaster && commissioningComponentSelection.name === "enclosure"

						if (error.response?.body?.non_field_errors || error.response?.body?.field_errors) {
							// these are Django serializer errors. There's a possibility of any number of these, under different keys
							// If we have any of these, we want to NOT store the Node Board, and inform the user
							// format is { "non_field_errors": ["inputError message 1", "inputError message 2"], "field_errors": ["inputError message 1", "inputError message 2"] }
							setComponentSelection(commissioningComponentSelection, "failed")
							const nonFieldErrors = error.response?.body?.non_field_errors
							const fieldErrors = error.response?.body?.field_errors
							let errorStrings = [...(nonFieldErrors || []), ...(fieldErrors || [])]
							let message = ""
							for (const value of errorStrings) {
								message += `- ${value}\n`
							}
							setFeedbackHandler({
								message: message,
								type: "error",
							})
							if (!dontReopenCondition) {
								handleScannerReOpen()
							}
						} else if (discardStatuses.includes(error.response?.status)) {
							// these statuses are presumed to be failures we don't want to save
							setComponentSelection(commissioningComponentSelection, "failed")
							setFeedbackHandler({
								message: "Failed to cache request while offline.",
								type: "info",
							})
							if (error.response?.statusText) {
								console.error(error.response?.statusText)
								const message = error.response?.statusText
								setFeedbackHandler({
									message: message,
									type: "error",
								})
							}
							if (!dontReopenCondition) {
								handleScannerReOpen()
							}
						} else {
							setComponentSelection(commissioningComponentSelection, "pending")
							const url = `/controllers/${controllerType}s/${controllerUuid}/components/${commissioningComponentSelection.urlName}/`
							const request = {
								url: url,
								method: HttpMethod.POST,
								payload: scannedData,
								queryArgs: {},
								headers: {},
								raiseException: true,
							}
							// format is (storeName, value, key)
							await (await offlineStorage).put("apiRequests", request, url)
							const message = `Successfully cached ${controllerType} ${commissioningComponentSelection.name} request to be sent later`
							setFeedbackHandler({
								message: message,
								type: "info",
							})
						}
					})
			}
			// reset scanned data to null after submitting
			setScannedData(null)
		}, [
			handleScannerReOpen,
			scannedData,
			commissioningComponentSelection,
			setComponentSelection,
			setFeedbackHandler,
			nodeIdDuplicateWarning,
			controllerUuid,
			masterIdDuplicateWarning,
			handleAcceptHandler,
			isMaster,
		])

		return !scanning ? (
			<DialogContent className={`${deviceIdEntryMethod === "text" ? styles.content : styles.scannerContent}`}>
				{isMaster && (
					<>
						{!isDeviceIdAssignment && (
							<>
								<ScannerHeaderComponent
									scanning={scanning}
									handleScannerClose={handleScannerClose}
									isMaster={isMaster}
									masterUuid={masterUuid}
									masterDetails={masterDetails}
									nodeControllerUuidToDisplay={nodeControllerUuidToDisplay}
									userMessage={userMessage}
								/>
								<TextField
									id="deviceid-input"
									variant="outlined"
									className={styles.textInputBox}
									type="text"
									label={`${masterOrNode} ID`}
									onChange={handleIdChange}
									value={newDeviceId || ""}
									error={!!newDeviceId && inputFormattingError}
									helperText={inputValidationHelpText}
									autoFocus={deviceIdEntryMethod === "text" || !scannerSupported}
									InputProps={!isMaster ? { endAdornment } : {}}
								/>
								{newDeviceId !== "" && !masterIdDuplicateWarning && (
									<DialogActions className={styles.actions}>
										<Button onClick={handleClose} className={styles.actionButton}>
											Close
										</Button>
										<Button
											variant="contained"
											color="primary"
											type="submit"
											disabled={inputFormattingError}
											className={styles.actionButton}
											onClick={handleAcceptHandler}
										>
											Accept
										</Button>
									</DialogActions>
								)}
							</>
						)}
						{isDeviceIdAssignment && (
							<>
								<ScannerHeaderComponent
									scanning={scanning}
									handleScannerClose={handleScannerClose}
									isMaster={isMaster}
									masterUuid={masterUuid}
									masterDetails={masterDetails}
									nodeControllerUuidToDisplay={nodeControllerUuidToDisplay}
									userMessage={userMessage}
								/>
								<SelectComponent
									isMaster={isMaster}
									masterComponentScanned={masterComponentScanned}
									nodeComponentScanned={nodeComponentScanned}
									nodeDetails={nodeDetails}
									masterUuid={masterUuid}
									nodeLocIdFromUrl={nodeLocIdFromUrl}
									handleNodeComponentSelection={handleComponentSelection}
									setDeviceIdEntryMethod={setDeviceIdEntryMethod}
									setNodeIdReplacement={setNodeIdReplacement}
									componentListSelectionHandler={componentListSelectionHandler}
								/>
								<RememberSelectionBtn
									handleRememberComponentSelection={handleRememberComponentSelection}
									rememberComponentSelection={rememberComponentSelection}
									handleComponentSelection={handleComponentSelection}
								/>
							</>
						)}
					</>
				)}
				{!isMaster && (
					<>
						<div className={styles.scannerModalContainer}>
							<ScannerHeaderComponent
								scanning={scanning}
								handleScannerClose={handleScannerClose}
								isMaster={isMaster}
								masterDetails={masterDetails}
								nodeControllerUuidToDisplay={nodeControllerUuidToDisplay}
								userMessage={userMessage}
							/>
							<TextField
								id="deviceid-input"
								variant="outlined"
								className={styles.textInputBox}
								type="text"
								label={`Enter new ${masterOrNode} ID`}
								onChange={handleIdChange}
								value={newDeviceId || ""}
								error={!!newDeviceId && inputFormattingError}
								helperText={inputValidationHelpText}
								autoFocus={deviceIdEntryMethod === "text" || !scannerSupported}
								InputProps={controllerUuid ? { endAdornment } : {}}
							/>
							{newDeviceId !== "" && !nodeIdDuplicateWarning && (
								<DialogActions className={styles.actions}>
									<Button onClick={handleClose} className={styles.actionButton}>
										Close
									</Button>
									<Button
										variant="contained"
										color="primary"
										type="submit"
										disabled={inputFormattingError}
										className={styles.actionButton}
										onClick={handleAcceptHandler}
									>
										Accept
									</Button>
								</DialogActions>
							)}
						</div>

						{isDeviceIdAssignment && (
							<>
								<div className={`${styles.scannerModalContainer}`}>
									<SelectComponent
										isMaster={isMaster}
										masterComponentScanned={masterComponentScanned}
										nodeComponentScanned={nodeComponentScanned}
										nodeDetails={nodeDetails}
										masterUuid={masterUuid}
										nodeLocIdFromUrl={nodeLocIdFromUrl}
										handleNodeComponentSelection={handleComponentSelection}
										setDeviceIdEntryMethod={setDeviceIdEntryMethod}
										setNodeIdReplacement={setNodeIdReplacement}
										componentListSelectionHandler={componentListSelectionHandler}
									/>
								</div>
								<div className={styles.nodeLocationNavContainer}>
									<NodeLocationNavigator
										handleScannerOpen={handleScannerOpen}
										commissioningComponentSelection={commissioningComponentSelection}
										positionAbsolute={false}
										displayOpenBtn={false}
									/>
								</div>
							</>
						)}
					</>
				)}
			</DialogContent>
		) : (
			<div className={styles.scannerParentContainer}>
				<ScannerHeaderComponent
					scanning={scanning}
					handleScannerClose={handleScannerClose}
					isMaster={isMaster}
					masterDetails={masterDetails}
					nodeControllerUuidToDisplay={nodeControllerUuidToDisplay}
					userMessage={userMessage}
				/>
				{deviceIdEntryMethod === "barCode" && (
					<ReactQuagga
						onDetected={handleCodeDetection}
						onClose={handleScannerClose}
						onError={(err) => handleScanError(err)}
					/>
				)}
				{deviceIdEntryMethod === "qrCode" && (
					<ReactQrReader
						onDetected={handleCodeDetection}
						onClose={handleScannerClose}
						onError={(err) => handleScanError(err)}
					/>
				)}
			</div>
		)
	},
)

DeviceIdInput.displayName = "DeviceIdInput"

DeviceIdInput.propTypes = {
	handleClose: PropTypes.func.isRequired,
	deviceIdEntryMethod: PropTypes.string,
	setDeviceIdEntryMethod: PropTypes.func,
	locIds: PropTypes.string.isRequired,
	handleAccept: PropTypes.func.isRequired,
	nodeDetails: PropTypes.object,
	masterDetails: PropTypes.object,
	nodeComponentScanned: PropTypes.object,
	nodeLocIdFromUrl: PropTypes.string,
}

const DialogDeviceIdInput = memo((props) => {
	const [deviceIdEntryMethod, setDeviceIdEntryMethod] = useState(null)
	const scannerSupported = useQuagga()
	const isOpen = props.locIds.length > 0
	const isDeviceIdAssignment = props.location.search.split("&").includes("overlay=deviceid-assignment")

	return (
		<Dialog
			open={isOpen}
			TransitionComponent={Transition}
			keepMounted={false}
			onClose={props.handleClose}
			aria-labelledby="device-id-input-title"
			aria-describedby="device-id-input-description"
			fullWidth={true}
			hideBackdrop={true}
		>
			{isOpen && (
				<DeviceIdInput
					nodeComponentScanned={props.nodeComponentScanned}
					masterComponentScanned={props.masterComponentScanned}
					isDeviceIdAssignment={isDeviceIdAssignment}
					scannerSupported={scannerSupported}
					deviceIdEntryMethod={deviceIdEntryMethod}
					setDeviceIdEntryMethod={setDeviceIdEntryMethod}
					{...props}
				/>
			)}
		</Dialog>
	)
})

DialogDeviceIdInput.displayName = "DialogDeviceIdInput"
DialogDeviceIdInput.propTypes = DeviceIdInput.propTypes

export default DialogDeviceIdInput
